/**
 * Viewport width from one shared `resize` listener, updated only when the width actually
 * changed: on iOS every scroll gesture fires a height-only resize, which used to fan out to one
 * handler per carousel instance. Carousels are the only consumer so far - the other resize
 * consumers still attach their own listener.
 *
 * Not VueUse's `createSharedComposable(useWindowSize)`: that notifies on every resize, height
 * changes included.
 */
import { readonly } from 'vue'
import { useNuxtApp, useState } from '#imports'

const VIEWPORT_WIDTH_STATE_KEY = 'viewport-width'

export function useSharedViewportWidth() {
    const viewportWidth = useState<number>(VIEWPORT_WIDTH_STATE_KEY, () => 0)

    if (import.meta.client) {
        // The flag lives on the Nuxt app instance, so the listener is attached once per app
        // and not once per consumer. It stays for the app's lifetime.
        const nuxtApp = useNuxtApp()

        if (!nuxtApp._viewportWidthListening) {
            nuxtApp._viewportWidthListening = true

            viewportWidth.value = window.innerWidth

            window.addEventListener('resize', () => {
                // Height-only changes (iOS URL bar) must not notify consumers.
                if (window.innerWidth === viewportWidth.value) return

                viewportWidth.value = window.innerWidth
            })
        }
    }

    return readonly(viewportWidth)
}
